/**
 * Sebutan unit terkecil on the screens (owner, 2026-10-04; uji D5): the word each parish on this
 * device uses for its smallest unit ("Lingkungan", "Kombas", …), read once for the whole app
 * from the device's `pengaturan_paroki` rows (`pengaturan:sebutan`) and again after every save
 * or pulled page, as the header's logo is. A screen asks `useSebutanUnit()` for the word of
 * the parish it works in (an admin keuskupan's diocese view: the default), or of a parish it
 * names (a diocese view where a parish is in context); `useTeksUnit` hands it a part of the
 * strings with `{unit}` and `{unitKecil}` filled.
 *
 * The shell loads it (./muatSebutanUnit); outside the shell (a test, a sign-in screen) the
 * default word stands. No database here: pure modules (a screen's words) may read the store.
 */
import { isiUnit, type SebutanUnit, sebutanUnit } from '@satu/shared'
import { useMemo } from 'react'
import { create } from 'zustand'
import { isKeuskupan, useSesi } from './sesi'

interface SebutanUnitState {
  /** The stored word of each parish on this device, by parish id. */
  peta: Readonly<Record<string, string>>
}

export const useSebutanUnitStore = create<SebutanUnitState>(() => ({ peta: {} }))

/** The word of `parokiId` on this device; the default without a parish or a stored word. */
export function sebutanParoki(parokiId: string | null | undefined): SebutanUnit {
  const peta = useSebutanUnitStore.getState().peta
  return sebutanUnit(parokiId === null || parokiId === undefined ? null : peta[parokiId])
}

/** The parish a screen works in: none in a diocese view (the default word). */
function parokiKerja(s: ReturnType<typeof useSesi.getState>): string | null {
  return isKeuskupan(s.sesi.peran) ? null : s.sesi.parokiId
}

/** The word of the parish the session works in, outside React (a handler, a download). */
export function sebutanKini(): SebutanUnit {
  return sebutanParoki(parokiKerja(useSesi.getState()))
}

/**
 * The word of `parokiId`, or, when it is omitted, of the parish the session works in. A view
 * with no parish in context (null) has the default.
 */
export function useSebutanUnit(parokiId?: string | null): SebutanUnit {
  const kerja = useSesi(parokiKerja)
  const id = parokiId === undefined ? kerja : parokiId
  const kata = useSebutanUnitStore((s) => (id === null ? undefined : s.peta[id]))
  return sebutanUnit(kata)
}

/** `teks` (a string or a part of the strings) in the parish's word; the same object per word. */
export function useTeksUnit<T>(teks: T, parokiId?: string | null): T {
  const sebutan = useSebutanUnit(parokiId)
  return useMemo(() => isiUnit(teks, sebutan), [teks, sebutan])
}
